/**
 * The invite link's page (Phase 7b): whose invite it is (the tenant, the
 * email), a new password typed twice against the pool's policy, and — once
 * set — the way to the sign-in page. A link that is used, replaced, expired or
 * mistyped says so, without saying which (the server does not either).
 */
import { newPasswordSchema } from '@acp/api-contract/tenancy.js';
import { type FormEvent, useState } from 'react';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';

export type AcceptInviteState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'invalid'; readonly message: string }
  | { readonly kind: 'ready'; readonly email: string; readonly tenantName: string; readonly expiresAt: string }
  | { readonly kind: 'done'; readonly email: string };

export type AcceptInviteFormProps = {
  readonly state: AcceptInviteState;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (password: string) => void;
  readonly onSignIn: () => void;
};

/** What the password still lacks, per the pool's policy (the contract's schema), or []. */
export function passwordProblems(password: string): string[] {
  const parsed = newPasswordSchema.safeParse(password);
  return parsed.success ? [] : parsed.error.issues.map((i) => i.message);
}

function Ready({ state, pending, error, onSubmit }: { readonly state: Extract<AcceptInviteState, { kind: 'ready' }>; readonly pending: boolean; readonly error: string | null; readonly onSubmit: (password: string) => void }) {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const problems = passwordProblems(password);
  const ok = problems.length === 0 && password === again;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ok && !pending) onSubmit(password);
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <p className="text-sm">
        You are invited to <strong>{state.tenantName}</strong> as <strong>{state.email}</strong>. Choose a password; the link then stops working.
      </p>
      <div className="grid gap-1">
        <Label htmlFor="invite-password">New password</Label>
        <Input id="invite-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="invite-password-again">Repeat it</Label>
        <Input id="invite-password-again" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} data-testid="invite-password-again" />
      </div>
      {password !== '' && problems.length > 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="password-problems">
          Needs: {problems.join(', ')}.
        </p>
      ) : null}
      {again !== '' && password !== again ? <p className="text-sm text-destructive">The two do not match.</p> : null}
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Button type="submit" disabled={!ok || pending} data-testid="invite-accept">
        {pending ? 'Setting…' : 'Set password'}
      </Button>
      <p className="text-xs text-muted-foreground">The link works until {state.expiresAt.slice(0, 16).replace('T', ' ')} UTC.</p>
    </form>
  );
}

export function AcceptInviteForm({ state, pending, error, onSubmit, onSignIn }: AcceptInviteFormProps) {
  return (
    <Card className="mx-auto w-full max-w-md" data-testid="accept-invite" data-state={state.kind}>
      <CardHeader>
        <CardTitle>Set your password</CardTitle>
        <CardDescription>Your invitation to ACP.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {state.kind === 'loading' ? <p className="text-sm text-muted-foreground">Checking your invite…</p> : null}
        {state.kind === 'invalid' ? (
          <p role="alert" className="text-sm text-destructive" data-testid="invite-invalid">
            {state.message}
          </p>
        ) : null}
        {state.kind === 'ready' ? <Ready state={state} pending={pending} error={error} onSubmit={onSubmit} /> : null}
        {state.kind === 'done' ? (
          <div className="grid gap-3" role="status">
            <p className="text-sm">Your password is set. Sign in as {state.email}.</p>
            <Button onClick={onSignIn} data-testid="go-sign-in">
              Go to sign in
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
